import { useSyncExternalStore } from "react";

/**
 * Panel font. The default comes from admin settings («ظاهر پنل‌ها», `ui.font`, public
 * `GET /api/v1/appearance`). In the admin panel each admin may pick their own in the header's
 * display menu, stored in this browser like the theme; the partner panel always follows the default. Result: `html[data-font]` (see globals.css).
 */
export type PanelFont = "iranyekan" | "iransans" | "pinar" | "abar" | "lahzeh";
export type FontPreference = PanelFont | "system";

export const PANEL_FONTS: { id: PanelFont; label: string }[] = [
    { id: "iranyekan", label: "ایران یکان" },
    { id: "iransans", label: "ایران سنس" },
    { id: "pinar", label: "پینار" },
    { id: "abar", label: "ابر" },
    { id: "lahzeh", label: "لحظه" },
];

const SYSTEM_KEY = "mfa-font";
const USER_KEY = "mfa-aff:font";

export const isPanelFont = (v: unknown): v is PanelFont => PANEL_FONTS.some((f) => f.id === v);
const isFont = isPanelFont;

const read = (key: string): string | null => {
    try {
        return window.localStorage.getItem(key);
    } catch {
        return null;
    }
};
const write = (key: string, value: string) => {
    try {
        window.localStorage.setItem(key, value);
    } catch {
        /* blocked storage: the choice lasts for this tab */
    }
};

let systemFont: PanelFont = "iranyekan";
let preference: FontPreference = "system";
/** Off in the partner panel: a personal choice is neither read nor offered there. */
let personal = false;
if (typeof window !== "undefined") {
    const s = read(SYSTEM_KEY);
    if (isFont(s)) systemFont = s;
}

const listeners = new Set<() => void>();
const effective = (): PanelFont => (preference === "system" ? systemFont : preference);

function render() {
    const font = effective();
    if (font === "iranyekan") delete document.documentElement.dataset.font;
    else document.documentElement.dataset.font = font;
    listeners.forEach((l) => l());
}

/** The system default (admin settings); a personal choice still wins. */
export function applyFont(font: PanelFont): void {
    systemFont = font;
    write(SYSTEM_KEY, font);
    render();
}

export function setFontPreference(next: FontPreference): void {
    preference = next;
    write(USER_KEY, next);
    render();
}

/** `{ preference, systemFont, font, setPreference }`; re-renders on change. */
export function useFont() {
    const subscribe = (l: () => void) => {
        listeners.add(l);
        return () => listeners.delete(l);
    };
    const pref = useSyncExternalStore(subscribe, () => preference, () => "system" as FontPreference);
    const sys = useSyncExternalStore(subscribe, () => systemFont, () => "iranyekan" as PanelFont);
    return { preference: pref, systemFont: sys, font: pref === "system" ? sys : pref, setPreference: setFontPreference };
}

/** Whether this app lets each person pick their own font (admin panel only). */
export const personalFontAllowed = () => personal;

/**
 * Call once at start-up, before rendering: applies the remembered font, then the server default.
 * `personal`: honour and offer a personal choice (the admin panel); otherwise always the default.
 */
export function bootAppearance(opts: { personal?: boolean } = {}): void {
    personal = opts.personal === true;
    if (personal) {
        const p = read(USER_KEY);
        if (isFont(p)) preference = p;
    }
    render();
    window.addEventListener("storage", (e) => {
        if (e.key !== USER_KEY && e.key !== SYSTEM_KEY) return;
        const p = personal ? read(USER_KEY) : null;
        preference = isFont(p) ? p : "system";
        const s = read(SYSTEM_KEY);
        if (isFont(s)) systemFont = s;
        render();
    });
    void fetch("/api/v1/appearance", { credentials: "omit" })
        .then((r) => (r.ok ? (r.json() as Promise<{ font?: string }>) : null))
        .then((d) => {
            if (isFont(d?.font)) applyFont(d.font);
        })
        .catch(() => {});
}
